/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps } from '@fluentui/react';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DonutChart } from 'src/core/components/DonutChart';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { LegendColor, LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import { Row } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type OverallIntentStatsProps = {
    /**
     * The select faulty intent.
     */
    selectedIntent: IntentMetadata;

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    totalUtterancesLabel: {
        id: 'analytics.faultyIntentDetails.totalUtterancesLabel',
        defaultMessage: 'Total utterances'
    },
    correctlyPredicted: {
        id: 'analytics.faultyIntentDetails.correctlyPredicted',
        defaultMessage: 'Correctly predicted'
    },
    incorrectlyPredicted: {
        id: 'analytics.faultyIntentDetails.incorrectlyPredicted',
        defaultMessage: 'Incorrectly predicted'
    },
    unclearPredicted: {
        id: 'analytics.faultyIntentDetails.unclearPredicted',
        defaultMessage: 'Unclear predicted'
    },
    correctLabel: {
        id: 'analytics.faultyIntentDetails.correctLabel',
        defaultMessage: 'correct'
    }
});

const { styleDiv, styleComponent } = getStylist('Dashboard');

const Label = styleDiv<{ isBold?: boolean }>('HeaderLabel', {}, ({ customProps: { isBold } }, theme) => ({
    display: 'table-cell',
    verticalAlign: 'middle',
    fontSize: theme.labelFontSize,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing,
    fontWeight: isBold ? (theme.boldFont as Property.FontWeight) : (theme.semiboldFont as Property.FontWeight),
    $nest: {
        button: {
            verticalAlign: 'middle'
        }
    }
}));

const NumericLabel = styleComponent<ILabelProps>(Label)('NumericLabel', theme => ({
    paddingLeft: theme.detailedFaultyIntentLegendSpacing,
    textAlign: 'right'
}));

const PercentageLabel = styleComponent<ILabelProps>(Label)('PercentageLabel', theme => ({
    paddingLeft: theme.detailedFaultyIntentPercentLegendSpacing
}));

const StyledLink = styleComponent<ILinkProps>(Link)('StyledLink', theme =>
    important({ color: theme.correctColor, textDecoration: 'none' })
);

/**
 * Displays detailed information about the utterances of the selected intent.
 */
export const OverallIntentStats = (props: OverallIntentStatsProps) => {
    const { selectedIntent, onIntentClick } = props;
    const { localize } = useLocalization();

    const xData: ChartAxisData<string> = {
        label: 'Type',
        data: ['Correctly predicted', 'Unclear', 'Incorrectly predicted']
    };

    const yData: ChartAxisData<number> = {
        label: 'Ratio',
        data: [selectedIntent.correctAccuracy, selectedIntent.unclearAccuracy, selectedIntent.incorrectAccuracy]
    };

    const donutTitle = `${selectedIntent.correctAccuracy.toFixed(1)}%`;

    return (
        <Stack horizontal tokens={{ childrenGap: 40 }} verticalAlign="center">
            <DonutChart
                width={40}
                xData={xData}
                yData={yData}
                donutTitle={donutTitle}
                donutSubtitle={localize(messages.correctLabel)}
                size="medium"
            />
            <StackItem grow={1}>
                <div>
                    <Row>
                        <Label customProps={{ isBold: true }}>{localize(messages.totalUtterancesLabel)}</Label>
                        <NumericLabel customProps={{ isBold: true }}>{selectedIntent.numUtterances}</NumericLabel>
                    </Row>

                    <StatsRow
                        color="correct"
                        selectedIntent={selectedIntent}
                        label={localize(messages.correctlyPredicted)}
                        displayedAccuracy={selectedIntent.correctAccuracy}
                        displayedNumber={selectedIntent.numCorrectUtterances}
                        onIntentClick={id => onIntentClick(id, { differenceFilters: ['correct'], nearestRivalIntentsFilters: [] })}
                    />

                    <StatsRow
                        color="incorrect"
                        selectedIntent={selectedIntent}
                        label={localize(messages.incorrectlyPredicted)}
                        displayedAccuracy={selectedIntent.incorrectAccuracy}
                        displayedNumber={selectedIntent.numIncorrectUtterances}
                        onIntentClick={id => onIntentClick(id, { differenceFilters: ['incorrect'], nearestRivalIntentsFilters: [] })}
                    />

                    <StatsRow
                        color="unclear"
                        selectedIntent={selectedIntent}
                        label={localize(messages.unclearPredicted)}
                        displayedAccuracy={selectedIntent.unclearAccuracy}
                        displayedNumber={selectedIntent.numUnclearUtterances}
                        onIntentClick={id => onIntentClick(id, { differenceFilters: ['unclear'], nearestRivalIntentsFilters: [] })}
                    />
                </div>
            </StackItem>
        </Stack>
    );
};

/**
 * Represents the common case of the row in this component.
 */
const StatsRow = (props: {
    selectedIntent: IntentMetadata;
    color: LegendColor;
    label: string;
    displayedNumber: number;
    displayedAccuracy: number;
    onIntentClick: (intentId: LuisId) => void;
}) => {
    const { color, label, displayedNumber, displayedAccuracy, onIntentClick, selectedIntent } = props;

    return (
        <Row>
            <Label>
                <LegendItem color={color} />
                <StyledLink onClick={() => onIntentClick(selectedIntent.intentId)}>{label}</StyledLink>
            </Label>
            <NumericLabel customProps={{ isBold: true }}>{displayedNumber}</NumericLabel>
            <PercentageLabel>{displayedAccuracy.toFixed(2)}%</PercentageLabel>
        </Row>
    );
};
